/*
 * Consent checkboxes and the contract modal.
 *
 * 1. <hdt-agree-gate form-id="…"> — same job as the theme's <hdt-need-checked-to-use>,
 *    but gated on *every* required agreement checkbox of the form instead of a single
 *    element id, so accelerated checkout buttons stay disabled until all are ticked.
 *
 * 2. Contract modal — a link inside a consent label opens the document in a dialog
 *    instead of navigating away. The page body is fetched from `?view=contract`
 *    (templates/page.contract.liquid) and the [[…]] placeholders left in it are filled
 *    from the token island rendered by snippets/cart-contract-data.liquid, so a shopper
 *    approving the Mesafeli Satış Sözleşmesi sees their own order in it.
 */
(function () {
  const CONSENT_LINK_SELECTOR = '.hdt-cart-checkbox a[href], .hdt-consent-checkbox a[href]';

  /* ------------------------------------------------------------------ gate */

  if (!customElements.get('hdt-agree-gate')) {
    class AgreeGate extends HTMLElement {
      connectedCallback() {
        this.controller = new AbortController();
        const { signal } = this.controller;

        document.addEventListener(
          'change',
          (event) => {
            if (event.target && event.target.name === 'agree_checkbox') this.sync();
          },
          { signal }
        );

        this.addEventListener(
          'click',
          () => {
            if (this.allChecked()) return;
            const form = this.closest('form') || document.forms[this.formId];
            if (form) form.dispatchEvent(new Event('submit', { bubbles: true }));
          },
          { signal }
        );

        this.sync();
        requestAnimationFrame(() => this.sync());
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
      }

      get formId() {
        return this.getAttribute('form-id');
      }

      get checkboxes() {
        return Array.from(
          document.querySelectorAll(
            'input[type="checkbox"][name="agree_checkbox"][form="' + this.formId + '"]'
          )
        );
      }

      allChecked() {
        return this.checkboxes.every((input) => input.checked);
      }

      sync() {
        const target = this.firstElementChild;
        if (target) target.toggleAttribute('disabled', !this.allChecked());
      }
    }

    customElements.define('hdt-agree-gate', AgreeGate);
  }

  /* ---------------------------------------------------------------- modal */

  if (window.hokisContractModal) return;

  const documentCache = new Map();
  let dialog = null;
  let titleEl = null;
  let bodyEl = null;
  let acceptEl = null;
  let activeCheckbox = null;

  function buildDialog() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'hdt-contract-modal';
    dialog.innerHTML =
      '<div class="hdt-contract-modal__head">' +
      '<h3 class="hdt-contract-modal__title"></h3>' +
      '<button type="button" class="hdt-contract-modal__close" aria-label="Kapat">&times;</button>' +
      '</div>' +
      '<div class="hdt-contract-modal__body" tabindex="0"></div>' +
      '<div class="hdt-contract-modal__foot">' +
      '<button type="button" class="hdt-btn hdt-btn-accent hdt-btn--size-m hdt-contract-modal__accept">' +
      '<span>Okudum, onaylıyorum</span></button>' +
      '</div>';

    titleEl = dialog.querySelector('.hdt-contract-modal__title');
    bodyEl = dialog.querySelector('.hdt-contract-modal__body');
    acceptEl = dialog.querySelector('.hdt-contract-modal__accept');

    dialog.querySelector('.hdt-contract-modal__close').addEventListener('click', () => dialog.close());

    acceptEl.addEventListener('click', () => {
      if (activeCheckbox && !activeCheckbox.checked) {
        activeCheckbox.checked = true;
        activeCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
      dialog.close();
    });

    // Backdrop click closes — the dialog itself is the only click target outside its children.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    document.body.appendChild(dialog);
    return dialog;
  }

  function tokensFor(checkbox) {
    const formId = checkbox && checkbox.getAttribute('form');
    const island =
      (formId && document.querySelector('[data-contract-tokens][data-form="' + formId + '"]')) ||
      document.querySelector('[data-contract-tokens]');

    if (!island) return {};
    try {
      return JSON.parse(island.textContent);
    } catch (error) {
      return {};
    }
  }

  function fillTokens(html, tokens) {
    return Object.keys(tokens).reduce(
      (result, token) => result.split(token).join(tokens[token]),
      html
    );
  }

  // Contract pages are usually pasted from Word, so their tables carry hard pixel
  // widths. CSS forces them fluid; this gives the stubborn ones somewhere to scroll
  // instead of stretching the whole modal sideways on a phone.
  function makeTablesScrollable(root) {
    root.querySelectorAll('table').forEach((table) => {
      if (table.parentElement && table.parentElement.classList.contains('hdt-contract-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'hdt-contract-scroll';
      table.replaceWith(wrapper);
      wrapper.appendChild(table);
    });
    // Word exports also love inline widths on the wrapping blocks themselves.
    root.querySelectorAll('[width], [style*="width"]').forEach((node) => {
      if (node.tagName === 'IMG') return;
      node.removeAttribute('width');
      if (node.style && node.style.width) node.style.width = '';
      if (node.style && node.style.minWidth) node.style.minWidth = '';
    });
  }

  async function loadDocument(pathname) {
    if (documentCache.has(pathname)) return documentCache.get(pathname);

    const response = await fetch(pathname + '?view=contract', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Contract page request failed: ' + response.status);

    const html = await response.text();
    documentCache.set(pathname, html);
    return html;
  }

  async function openContract(link, checkbox) {
    const url = new URL(link.getAttribute('href'), window.location.origin);
    buildDialog();

    activeCheckbox = checkbox;
    titleEl.textContent = link.textContent.trim();
    bodyEl.innerHTML = '<p class="hdt-contract-modal__loading">Yükleniyor…</p>';
    acceptEl.hidden = !checkbox || checkbox.checked;
    dialog.showModal();
    bodyEl.scrollTop = 0;

    const html = await loadDocument(url.pathname);
    bodyEl.innerHTML = fillTokens(html, tokensFor(checkbox));

    const heading = bodyEl.querySelector('.hdt-contract-doc__title');
    if (heading) {
      titleEl.textContent = heading.textContent.trim();
      heading.remove();
    }
    makeTablesScrollable(bodyEl);
    bodyEl.scrollTop = 0;
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest(CONSENT_LINK_SELECTOR);
    if (!link) return;

    const href = link.getAttribute('href') || '';
    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (error) {
      return;
    }
    // Only same-shop content pages get the modal treatment; anything else navigates normally.
    if (!/\/pages\//.test(url.pathname)) return;

    event.preventDefault();

    const wrapper = link.closest('hdt-block-required') || link.closest('.hdt-consent-checkbox');
    const checkbox = wrapper && wrapper.querySelector('input[type="checkbox"]');

    openContract(link, checkbox).catch(() => {
      if (dialog) dialog.close();
      window.open(url.href, '_blank', 'noopener');
    });
  });

  window.hokisContractModal = { open: openContract };
})();
